import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isEqual, some } from "lodash";
import { v4 as uuid } from "uuid";

import Grid from '@mui/material/Unstable_Grid2';
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import { HOCList as List } from "../../common/components/List";
import { Placeholder } from "../../common/components/Placeholder";
import { ActionsGroup } from "../../common/components/ActionsGroup";

import { MappingDetail } from "./MappingsDetail";
import { MappingEditor } from "./MappingsEditor";
import { useDeleteMappingMutation, useGetMappingsQuery, usePostMappingMutation, usePutMappingMutation } from "./mappingsAPI";
import {
  setMapping,
  selectEditorErrors,
  selectCurrentMapping,
  selectPristineMapping
} from "./mappingsSlice";
import { selectPristineMachine } from "../machines/machinesSlice";

const newMapping = {
  id: uuid(),
  title: "",
  dsn: "",
  content: { feature_key1: "feature_value1" },
};

export const Mappings = ({ handleSlide }) => {
  const dispatch = useDispatch();

  const [mode, setMode] = useState('');
  
  const { dsn } = useSelector(selectPristineMachine);
  const editorErrors = useSelector(selectEditorErrors);
  const selectedMapping = useSelector(selectCurrentMapping);
  const pristineMapping = useSelector(selectPristineMapping);

  const { data = [], isLoading, isFetching } = useGetMappingsQuery(dsn, { skip: !dsn });
  const [postMapping, { isLoading: isPosting }] = usePostMappingMutation();
  const [putMapping, { isLoading: isPutting }] = usePutMappingMutation();
  const [deleteMapping, deleteResponse] = useDeleteMappingMutation();

  // Checking to enable save
  const isMappingSelected = !isEmpty(selectedMapping);
  const isSaveEnabled = () => {
    const isPristine = isEqual(pristineMapping, selectedMapping);
    const isValid = isEmpty(editorErrors);

    const { dsn, title } = selectedMapping;
    const hasRequireds = !some({ dsn, title }, isEmpty);

    return !isPristine && isValid && hasRequireds;
  };

  const handleSaveClick = () => {
    // handle creation or editing
    if (mode === "edit") {
      putMapping(selectedMapping);
    }
    if (mode === "create") {
      postMapping(selectedMapping);
    }
  };
  const handleNewClick = () => {
    setMode("create");
    dispatch(setMapping(newMapping));
  }
  const handleCancelClick = () => {
    setMode("");
    dispatch(setMapping({}));
  }
  const handleEditClick = (mapping) => {
    setMode("edit")
    dispatch(setMapping(mapping))
  }
  const handleDeleteClick = (mapping) => {
    deleteMapping(mapping);
  };

  return (
    <>
      <Grid container direction="row" spacing={1} columns={20}>
        <Grid xs={10} sx={{ display: "flex" }}>
          <List
            isLoading={isLoading || isFetching}
            data={data}
            header="Mappings"
            itemType="mapping"
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            onNewClick={handleNewClick}
          />
        </Grid>
        <Grid xs={10} sx={{ borderLeft: "1px solid", borderColor: "divider" }}>
          {isMappingSelected && <MappingDetail header="Mapping Detail" />}
          {!isMappingSelected && <Placeholder placeholderText="Select an from the list" height="35vh" /> }
        </Grid>
        <Grid xs={20} sc>
          <Divider variant="middle" flexItem>
            <ActionsGroup
              loading={isPutting || isPosting}
              generalDisable={!isMappingSelected}
              enableSave={isSaveEnabled()}
              onSaveClick={handleSaveClick}
              onCancelClick={handleCancelClick}
            />
          </Divider>
        </Grid>
        <Grid xs={20}>
          {isMappingSelected && <MappingEditor header="Mapping Json Editor" />}
          {!isMappingSelected && <Placeholder placeholderText="Select an from the list" height="35vh" /> }
        </Grid>
      </Grid>
    </>
  );
};
